import { curveLinear } from "d3-shape";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import ColorLegend from "../charts/lib/ColorLegend";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_learning_fig_PIRLSGAIN";
import { datasets } from "../data/data_learning_fig_PIRLSGAIN_datasets";
import { countryIdIso, getCountryName, mkGemEntityDecoder, useCountryEntities, useFigureControlItems, useFigureState, useMultiSelectState, useNamedEntities } from "../domain";
import { useMedia, useOrderedColorScale } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { O, pipe } from "../prelude";
export * from "../data/data_learning_fig_PIRLSGAIN";
const DEFAULT_COUNTRIES = [
    "MYS",
    "BHR"
];
const yUnitLabel = defineMessage({
    id: "fig.learning.PIRLSGAIN.yUnitLabel",
    defaultMessage: "Grade 4 students with minimum proficiency in reading"
});
export const Data = mkGemEntityDecoder([
    "ind_id",
    "year",
    "value"
], {});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedCountries] = useMultiSelectState("countries");
    const entityData = useNamedEntities(data);
    const countryData = useCountryEntities(entityData);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    React.useEffect(()=>{
        actions.setSelectionControl("countries", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Countries",
                defaultMessage: "Countries"
            })),
            selected: O.some(DEFAULT_COUNTRIES)
        }, countryControlItems);
    }, [
        intl,
        actions,
        env.countries,
        countryControlItems
    ]);
    const chartData = React.useMemo(()=>{
        const hasCountry = (country)=>pipe(selectedCountries, O.fold(()=>false, (xs)=>xs.includes(countryIdIso.unwrap(country))));
        return countryData.filter(({ id })=>hasCountry(id));
    }, [
        countryData,
        selectedCountries
    ]);
    const selectedCountryLabels = React.useMemo(()=>{
        return pipe(selectedCountries, O.map((xs)=>xs.map((x)=>getCountryName(env.countries, countryIdIso.wrap(x)))));
    }, [
        env.countries,
        selectedCountries
    ]);
    const { colorScale, colorLegendValues } = useOrderedColorScale(selectedCountryLabels);
    return <>
      <LineChart tLabel={(s)=>s} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} x="year" timeParse="%Y" timeFormat="%Y" numberFormat=".0%" yTicks={[
        0,
        0.2,
        0.4,
        0.6,
        0.8,
        1
    ]} category="datum.entity_name" colorLegend={false} colorScale={colorScale} connectLines values={chartData} markStyle={(x)=>x.entity_type === "world" ? "reference" : x.entity_type === "country" ? "normal" : "muted"} showTooltip curve={curveLinear} yAxisLabel={intl.formatMessage(yUnitLabel)} yAxisLabelPadding={24}/>
      <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>
    </>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_fig_PIRLSGAIN.json", import.meta.url).href,
    csv: new URL("../data/data_learning_fig_PIRLSGAIN.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_fig_PIRLSGAIN.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "narrow",
    datasets
});
